import { Link, useLocation } from "@tanstack/react-router"
import {
  BAKERY_ADDRESS,
  BAKERY_HOURS,
  BAKERY_PHONE,
  PREORDER_ENABLED,
} from "@/lib/constants/site"
import {
  CATERING_FAQ,
  CATERING_FAQ_BY_REQUEST,
} from "@/lib/data/catering-faq"
import { useCateringVisible } from "@/lib/hooks/use-site-settings"
import { getCountryCodeFromPath } from "@/lib/utils/region"
import { useMemo } from "react"

type FaqEntry = { q: string; a: string }
type FaqSection = { id: string; title: string; faqs: FaqEntry[] }

const BAKERY_ADDRESS_LINE = `${BAKERY_ADDRESS.street}, ${BAKERY_ADDRESS.city}, ${BAKERY_ADDRESS.region} ${BAKERY_ADDRESS.postalCode}`

// While the pre-order shop is off (PREORDER_ENABLED), every online order goes
// through Toast: same-day, pickup time shown by Toast. The pre-order answers
// below describe the site's own cart and checkout, so they only render when
// that shop is live — otherwise they tell guests to order 72 hours ahead
// through a cart that does not exist.
const TOAST_ORDERING_SECTION: FaqSection = {
  id: "ordering",
  title: "Ordering & Pickup",
  faqs: [
    {
      q: "How do I order ahead?",
      a: "Use the Order Online button to order through Toast for pickup at the café. Toast shows your estimated pickup time before you pay. You are also welcome to walk in — no reservation needed.",
    },
    {
      q: "Can I order for today?",
      a: `Yes. ${BAKERY_HOURS.kitchenNoticeLong} After 2 PM, hot kitchen items may show as unavailable on Toast.`,
    },
    {
      q: "Where do I pick up my order?",
      a: `At the café: ${BAKERY_ADDRESS_LINE}. We're open daily from 7:30 AM to 3:30 PM.`,
    },
    {
      q: "Can I change or cancel an online order?",
      a: `Call the café at ${BAKERY_PHONE} as soon as possible.`,
    },
    {
      q: "Do you offer delivery?",
      a: "Yes, through Toast. Use the Order Online button, choose Delivery, and enter your address.",
    },
  ],
}

const PREORDER_SECTION: FaqSection = {
  id: "pre-order",
  title: "Pre-Order & Pickup",
  faqs: [
    {
      q: "How far in advance do I need to place a pickup order?",
      a: "All pickup orders require a minimum of 72 hours (3 days) advance notice. This ensures your items are baked fresh specifically for your pickup. For weekend pickups, we recommend ordering by Wednesday.",
    },
    {
      q: "How does the pickup scheduling work?",
      a: "After adding items to your cart and proceeding to checkout, you'll be able to select your preferred pickup date and time. We offer 30-minute pickup windows between 7:30 AM and 3:00 PM, seven days a week.",
    },
    {
      q: "Where do I pick up my order?",
      a: "All orders are picked up at our Rancho Cucamonga location: 8685 Base Line Rd, Suite B, Rancho Cucamonga, CA 91730. We're open daily from 7:30 AM to 3:30 PM (kitchen orders until 2:00 PM; bakery items and drinks until 3:30 PM).",
    },
    {
      q: "Can I modify or cancel my order after placing it?",
      a: "We can accommodate modifications up to 24 hours before your scheduled pickup time. For cancellations made more than 24 hours before pickup, we offer a full refund. Cancellations within 24 hours are unfortunately non-refundable as your items will already be in production.",
    },
    {
      q: "What happens if I miss my pickup window?",
      a: "We'll hold your order for 30 minutes past your scheduled pickup time. If you're running late, please call us at (909) 256-4016 and we'll do our best to accommodate you. After 30 minutes, we cannot guarantee the order will still be available.",
    },
    {
      q: "Do you offer delivery?",
      a: "Pre-orders placed on this site are picked up at our Rancho Cucamonga café. For delivery, order through Toast using the Order Online button and choose Delivery.",
    },
  ],
}

// Products, coffee and general answers hold whichever way the flags are set;
// the two lines that depend on pre-orders switch with PREORDER_ENABLED.
const SHARED_SECTIONS: FaqSection[] = [
  {
    id: "products",
    title: "Products & Ingredients",
    faqs: [
      {
        q: "Are your products made fresh?",
        a: PREORDER_ENABLED
          ? "Yes — every item at +Bake Cafe is baked fresh to order. We don't keep pre-made items sitting around. This is why we require advance ordering — your food is made specifically for you."
          : "Yes. Our pastries are baked in-house every day, and kitchen items are made to order.",
      },
      {
        q: "Do you accommodate dietary restrictions?",
        a: "Some of our items can be made gluten-free or dairy-free upon request. Please note that our kitchen handles dairy, gluten, nuts, and eggs — we cannot guarantee a completely allergen-free environment. Please contact us directly to discuss your specific needs.",
      },
      {
        q: "What are the main flavors you use?",
        a: "We specialize in Filipino and Southeast Asian flavors including ube (purple yam), pandan (fragrant tropical leaf), coconut, calamansi, and black sesame — blended with Western baking techniques to create our unique fusion style.",
      },
      {
        q: "How long will my items stay fresh?",
        a: "Salt bread and pastries are best the day you pick them up. Cookies are best enjoyed within 2–3 days. Rice bowls and other kitchen items should be eaten the day of pickup.",
      },
    ],
  },
  {
    id: "coffee",
    title: "Coffee & Beans",
    faqs: [
      {
        q: "Who roasts +Bake Cafe's coffee?",
        a: "Our coffee comes from Local Coffee Roasters, a specialty roastery in Chino Hills, CA — about 15 minutes from the café. Every bag is roasted fresh weekly. The roastery began in 2018 as the coffee bar inside Take Ur Seat, our sister restaurant, and opened its own roastery in 2024, so our coffee program and our family of restaurants grew up together.",
      },
      {
        q: "What coffee is in your espresso?",
        a: "We pull our espresso on Brazil Minas Gerais Luzia, a natural-process coffee from the Cocatrel cooperative near Três Pontas, grown at 950–1,120 meters. Expect caramel, chocolate, and cream with a soft, nutty finish — it's the shot in every latte on our menu, from the Pandan Latte to the Spanish Latte.",
      },
      {
        q: "Do you sell whole bean coffee?",
        a: "Yes. We carry 12 oz bags of Local Coffee Roasters' current lineup — Brazil Minas Gerais Luzia, Ethiopia Yirgacheffe Aricha, and Colombia Sugarcane Decaf — at the same prices the roastery charges on its own website. Bags are roasted weekly, so you're taking home fresh coffee, not shelf stock.",
      },
      {
        q: "What is a pandan latte?",
        a: "Pandan is a fragrant Southeast Asian leaf with a naturally sweet, vanilla-like flavor. Our Pandan Latte pairs house-made pandan syrup with a double shot of espresso and steamed milk. It's one of our signature drinks and a good first taste of our Asian-fusion coffee style.",
      },
      {
        q: "Do you have sugar-free or decaf options?",
        a: "Both. Our Monk Fruit Vanilla Latte is sweetened entirely with monk fruit — no sugar. For decaf, we carry Colombia Sugarcane Decaf, a sugarcane-process decaf that keeps the coffee's natural sweetness. Ask at the counter about making your drink decaf or sugar-free.",
      },
      {
        q: "What roast level is +Bake's coffee?",
        a: "Light to medium, roasted in small batches. Coffee is a fruit, and roasting it lighter keeps the sweetness and brightness of the origin in the cup rather than burning it off for a generic dark-roast flavor. We roast our espresso that way too, so the fruit still comes through in a latte or cortado.",
      },
      {
        q: "How should I brew +Bake's beans at home?",
        a: "Our roaster's go-to is a pour-over. Use 24 g of coffee ground medium and 320 g of water at about 200°F (just off the boil) in a V60 dripper. Rinse the paper filter first, bloom with a small pour at the start, then pour in slow circles — aiming for a total brew time of 2:30 to 3:30. It takes about four minutes and brings out the floral, delicate side of the coffee. The full step-by-step is on our coffee page.",
      },
      {
        q: "Why does specialty coffee cost more than a regular cup?",
        a: "Good coffee passes through many hands before it reaches you — the grower, the mill, the importer, the roaster, and the barista. It's grown at elevation and usually harvested just once a year. Paying a bit more is how everyone in that chain, starting with the farmers, gets treated fairly — and it's why we work with a roaster who sources directly and roasts fresh.",
      },
    ],
  },
  {
    id: "general",
    title: "General",
    faqs: [
      {
        q: "What are your hours?",
        a: `We're open daily from 7:30 AM to 3:30 PM. Kitchen orders (made-to-order hot food) are accepted until 2:00 PM; bakery items and drinks are available until 3:30 PM. For walk-in visits, no reservation is needed. ${
          PREORDER_ENABLED
            ? "For pre-ordered pickups, your window will be confirmed at checkout."
            : "For online orders, Toast shows your pickup time before you pay."
        }`,
      },
      {
        q: "Is there seating at the café?",
        a: "Yes! We have a cozy café space with seating, board games available for guests, and a warm, welcoming atmosphere. We're a hangout, not just a pickup counter. Come stay a while.",
      },
      {
        q: "Do you do collaborations or pop-ups?",
        a: "We love connecting with local businesses and events! Reach out to us at talktome@takeurseat.com with your collaboration idea and we'll get back to you.",
      },
      {
        q: "How do I follow along with new items and specials?",
        a: "Follow us on Instagram @tusplusbake for the latest menu additions, seasonal specials, and behind-the-scenes baking content. We announce new items there first!",
      },
    ],
  },
]

/**
 * The sections /faq renders, for the current state of the two ordering
 * surfaces: the pre-order shop (build-time PREORDER_ENABLED) and the catering
 * page (runtime toggle in Admin → Settings → Store). Exported so the route
 * builds its FAQPage JSON-LD from the same call the page renders
 * (schema/content parity). Data, not a component — the fast-refresh rule
 * doesn't apply.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildFaqSections(cateringVisible: boolean): FaqSection[] {
  return [
    PREORDER_ENABLED ? PREORDER_SECTION : TOAST_ORDERING_SECTION,
    cateringVisible
      ? // Shared with /catering and its FAQPage schema — see lib/data/catering-faq.
        { id: "catering", title: "Catering Orders", faqs: CATERING_FAQ }
      : { id: "catering", title: "Catering", faqs: CATERING_FAQ_BY_REQUEST },
    ...SHARED_SECTIONS,
  ]
}

export default function FAQPage() {
  const location = useLocation()
  const countryCode = getCountryCodeFromPath(location.pathname) || "us"
  const cateringVisible = useCateringVisible()
  const sections = useMemo(
    () => buildFaqSections(cateringVisible),
    [cateringVisible],
  )

  return (
    <div className="bg-[#FFF9F8] min-h-screen">
      {/* Header */}
      <div className="bg-[#FCEAE7] border-b border-[#FBD1C8]/40 py-16 md:py-20">
        <div className="content-container text-center">
          <p className="section-eyebrow mb-3">Everything You Need to Know</p>
          <h1 className="section-title text-4xl md:text-5xl mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-[#5D5054] font-body text-base max-w-xl mx-auto">
            Have a question not answered here? Email us at{" "}
            <a
              href="mailto:talktome@takeurseat.com"
              className="text-[#F5928A] underline underline-offset-4 hover:text-[#F5928A]"
            >
              talktome@takeurseat.com
            </a>
          </p>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="content-container py-16 max-w-3xl mx-auto">
        {sections.map((section) => (
          <div key={section.id} className="mb-14" id={section.id}>
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[#FBD1C8]/60">
              <h2 className="font-display text-2xl text-[#5F5156]">{section.title}</h2>
              <div className="flex-1 h-px bg-[#FBD1C8]/40"></div>
            </div>

            <div className="space-y-3">
              {/* Native <details>: every answer is in the server-rendered HTML
                  (crawlers, no-JS visitors and assistive tech all get it), and
                  the shared `name` keeps one answer open at a time. */}
              {section.faqs.map((faq, i) => (
                <details
                  key={`${section.id}-${i}`}
                  name="faq"
                  className="group border border-[#FBD1C8]/40 bg-white rounded-2xl overflow-hidden"
                >
                  <summary className="w-full cursor-pointer list-none text-left px-4 py-5 flex items-start justify-between gap-4 sm:px-6 [&::-webkit-details-marker]:hidden">
                    <span className="text-[#5F5156] font-medium font-body text-sm md:text-base leading-snug">
                      {faq.q}
                    </span>
                    <span
                      aria-hidden="true"
                      className="flex-shrink-0 text-[#F5928A] text-xl leading-none transition-transform duration-200 group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="px-4 pb-6 border-t border-[#FBD1C8]/40 sm:px-6">
                    <p className="text-[#5D5054] font-body text-sm md:text-base leading-relaxed pt-4">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}

        {/* Still have questions */}
        <div className="bg-[#F5928A]/10 border border-[#F5928A]/20 rounded-3xl p-6 text-center mt-6 sm:p-8">
          <h3 className="font-display text-2xl text-[#5F5156] mb-3">
            Still have a question?
          </h3>
          <p className="text-[#5D5054] font-body text-sm mb-6">
            We're happy to help. Reach out and we'll get back to you as soon as we can.
          </p>
          <Link
            to="/$countryCode/contact"
            params={{ countryCode }}
            className="btn-coral w-full text-sm sm:w-auto"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  )
}
